import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, MapPinned, Undo2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg } from "../lib/api";
import { staffLabelText } from "./StaffAvatar";

const n = (v) => Number(v || 0).toLocaleString("en-IN");
const NONE = "__none__";

/**
 * Phase 156 — Mohalla Backfill: fill `mohalla_id` on a ward's properties that have none, so mohalla-level
 * collectors can see and receive them. Preview → apply (undoable). Assignments re-sync automatically.
 *   wardId   optional preselected ward (e.g. from an Unassigned Wards row)
 *   onDone   called after a successful apply / undo (so lists can refresh)
 */
export function MohallaBackfillDialog({ code, open, onOpenChange, wardId: initialWard = null, onDone }) {
  const [wards, setWards] = useState(null);
  const [wardId, setWardId] = useState(initialWard || "");
  const [strategy, setStrategy] = useState("address");
  const [mohallaId, setMohallaId] = useState(NONE);
  const [fallbackId, setFallbackId] = useState(NONE);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setPreview(null);
    setError(null);
    setWardId(initialWard || "");
    setStrategy("address");
    setMohallaId(NONE);
    setFallbackId(NONE);
    api.get(`/t/${code}/mohalla-backfill/wards`).then((r) => setWards(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, code, initialWard]);

  const loadRuns = React.useCallback(() => {
    if (!wardId) return setRuns([]);
    api.get(`/t/${code}/mohalla-backfill/runs`, { params: { ward_id: wardId } }).then((r) => setRuns(r.data || [])).catch(() => setRuns([]));
  }, [code, wardId]);

  useEffect(() => {
    if (!open || !wardId) { setPreview(null); return undefined; }
    setLoading(true);
    setError(null);
    const t = setTimeout(async () => {
      try {
        const params = { ward_id: wardId, strategy };
        if (strategy === "single" && mohallaId !== NONE) params.mohalla_id = mohallaId;
        if (strategy === "address" && fallbackId !== NONE) params.fallback_mohalla_id = fallbackId;
        if (strategy === "single" && mohallaId === NONE) { setPreview(null); setLoading(false); return; }
        const r = await api.get(`/t/${code}/mohalla-backfill/preview`, { params });
        setPreview(r.data);
      } catch (e) {
        setPreview(null);
        setError(errMsg(e));
      } finally { setLoading(false); }
    }, 250);
    loadRuns();
    return () => clearTimeout(t);
  }, [open, code, wardId, strategy, mohallaId, fallbackId, loadRuns]);

  const wardRows = wards?.rows || [];
  const wardRow = useMemo(() => wardRows.find((w) => w.ward_id === wardId), [wardRows, wardId]);
  const mohallas = preview?.mohallas || [];

  const apply = async (includeLow = true) => {
    setBusy(true);
    try {
      const body = { ward_id: wardId, strategy };
      if (strategy === "single") body.mohalla_id = mohallaId;
      if (strategy === "address" && fallbackId !== NONE) body.fallback_mohalla_id = fallbackId;
      if (strategy === "address") body.include_low_confidence = includeLow;   // Phase 187
      const r = await api.post(`/t/${code}/mohalla-backfill/apply`, body);
      setResult(r.data);
      toast.success(`${n(r.data.filled)} propert${r.data.filled === 1 ? "y" : "ies"} filled`, {
        description: r.data.sync?.moved || r.data.sync?.assigned
          ? `Assignments updated: ${n(r.data.sync.assigned)} assigned, ${n(r.data.sync.moved)} moved to the collector who now covers them`
          : "Assignments already matched the new mohallas",
      });
      api.get(`/t/${code}/mohalla-backfill/wards`).then((x) => setWards(x.data)).catch(() => {});
      loadRuns();
      onDone?.(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  // Phase 187: the Fill button asks for confirmation when any address match is low confidence
  const lowRows = useMemo(() => (strategy === "address" ? (preview?.rows || []).filter((r) => r.confidence === "low") : []), [preview, strategy]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const onFillClick = () => {
    if (strategy === "address" && (preview?.confidence?.low || 0) > 0) setConfirmOpen(true);
    else apply(true);
  };

  const undo = async (runId) => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/mohalla-backfill/runs/${runId}/undo`);
      toast.success(`Backfill undone — ${n(r.data.reverted)} propert${r.data.reverted === 1 ? "y" : "ies"} cleared`);
      setResult(null);
      api.get(`/t/${code}/mohalla-backfill/wards`).then((x) => setWards(x.data)).catch(() => {});
      loadRuns();
      onDone?.(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const canApply = !!preview && preview.will_fill > 0 && !busy && (strategy !== "single" || mohallaId !== NONE);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mohalla-backfill-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base"><MapPinned className="h-4 w-4" /> Mohalla Backfill</DialogTitle>
          <DialogDescription>
            Fill the mohalla on properties that have none, so collectors mapped to specific mohallas can see and receive them.
            Assignments re-sync automatically after each run; every run can be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Ward</Label>
            {!wards ? <Skeleton className="h-9 w-full" /> : (
              <Select value={wardId || NONE} onValueChange={(v) => { setWardId(v === NONE ? "" : v); setResult(null); setMohallaId(NONE); setFallbackId(NONE); }}>
                <SelectTrigger data-testid="backfill-ward-select"><SelectValue placeholder="Choose a ward" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Choose a ward</SelectItem>
                  {wardRows.map((w) => (
                    <SelectItem key={w.ward_id} value={w.ward_id} data-testid={`backfill-ward-option-${w.ward_id}`}>
                      {w.ward_name} — {n(w.no_mohalla)} without mohalla{w.alert ? " · no whole-ward collector" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {wards && wards.summary && (
              <p className="text-[11px] text-muted-foreground" data-testid="backfill-summary">
                {n(wards.summary.properties_without_mohalla)} propert{wards.summary.properties_without_mohalla === 1 ? "y" : "ies"} without a mohalla across {n(wards.summary.wards_with_gaps)} ward{wards.summary.wards_with_gaps === 1 ? "" : "s"}.
              </p>
            )}
          </div>

          {wardRow && (
            <div className={`rounded-lg border px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 ${wardRow.alert ? "border-amber-300/70 bg-amber-50/70 dark:bg-amber-950/20" : "bg-muted/40"}`} data-testid="backfill-ward-status">
              <span><span className="font-semibold">{n(wardRow.no_mohalla)}</span> of {n(wardRow.properties)} properties have no mohalla</span>
              <span>· {n(wardRow.mohallas)} mohalla{wardRow.mohallas === 1 ? "" : "s"} defined</span>
              {wardRow.whole_ward_collectors?.length > 0 && <span>· whole ward: {wardRow.whole_ward_collectors.map((c) => staffLabelText(c.name, c.employee_id)).join(", ")}</span>}
              {wardRow.mohalla_collectors?.length > 0 && <span>· by mohalla: {wardRow.mohalla_collectors.map((c) => staffLabelText(c.name, c.employee_id)).join(", ")}</span>}
              {wardRow.alert && <span className="text-amber-800 dark:text-amber-300 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> these {n(wardRow.no_mohalla)} have no covering collector today</span>}
            </div>
          )}

          {wardId && (
            <div className="space-y-2">
              <Label>How to fill</Label>
              <RadioGroup value={strategy} onValueChange={(v) => { setStrategy(v); setResult(null); }} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${strategy === "address" ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`} data-testid="backfill-strategy-address">
                  <RadioGroupItem value="address" className="mt-0.5" />
                  <span className="text-xs"><span className="font-medium block text-sm">Match the address</span>Each property whose address mentions one mohalla name gets that mohalla. Clear full-name matches are <span className="text-emerald-700 dark:text-emerald-300 font-medium">high confidence</span>; partial or conflicting mentions are <span className="text-orange-700 dark:text-orange-300 font-medium">low confidence</span> and need your confirmation. Rows mentioning several are skipped.</span>
                </label>
                <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${strategy === "single" ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`} data-testid="backfill-strategy-single">
                  <RadioGroupItem value="single" className="mt-0.5" />
                  <span className="text-xs"><span className="font-medium block text-sm">One mohalla for all</span>Every property without a mohalla in this ward gets the mohalla you pick.</span>
                </label>
              </RadioGroup>
              {strategy === "single" ? (
                <div className="space-y-1.5">
                  <Label>Mohalla</Label>
                  <Select value={mohallaId} onValueChange={setMohallaId}>
                    <SelectTrigger data-testid="backfill-mohalla-select"><SelectValue placeholder="Pick the mohalla" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Pick the mohalla</SelectItem>
                      {(mohallas.length ? mohallas : (preview?.mohallas || [])).map((m) => <SelectItem key={m.id} value={m.id}>{m.mohalla_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!mohallas.length && !preview && !loading && <MohallaLoader code={code} wardId={wardId} onLoaded={(list) => setPreview((p) => p || { mohallas: list, will_fill: 0, missing: wardRow?.no_mohalla || 0, matches: [] })} />}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>For addresses that match nothing <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Select value={fallbackId} onValueChange={setFallbackId}>
                    <SelectTrigger data-testid="backfill-fallback-select"><SelectValue placeholder="Leave them empty" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Leave them empty</SelectItem>
                      {mohallas.map((m) => <SelectItem key={m.id} value={m.id}>{m.mohalla_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {wardId && error && <p className="text-sm text-destructive" data-testid="backfill-error">{error}</p>}
          {wardId && loading && !preview && <Skeleton className="h-20 w-full" />}
          {preview && !result && (strategy !== "single" || mohallaId !== NONE) && (
            <div className="rounded-lg border p-3 space-y-2" data-testid="backfill-preview">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-medium flex items-center gap-1.5"><Wand2 className="h-4 w-4 text-primary" /> Preview</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {strategy === "address" && preview.confidence && (preview.confidence.high > 0 || preview.confidence.low > 0) && (
                    <>
                      <Badge variant="outline" className={`text-[11px] gap-1 ${CONF.high.cls}`} data-testid="backfill-confidence-high">
                        <CheckCircle2 className="h-3 w-3" /> {n(preview.confidence.high)} high confidence
                      </Badge>
                      <Badge variant="outline" className={`text-[11px] gap-1 ${preview.confidence.low ? CONF.low.cls : "bg-muted text-muted-foreground border-border"}`} data-testid="backfill-confidence-low">
                        <AlertTriangle className="h-3 w-3" /> {n(preview.confidence.low)} low confidence
                      </Badge>
                    </>
                  )}
                  <Badge variant={preview.will_fill ? "default" : "outline"} data-testid="backfill-will-fill">{n(preview.will_fill)} of {n(preview.missing)} will be filled</Badge>
                </div>
              </div>
              {preview.matches.length > 0 && (
                <ul className="text-xs space-y-1" data-testid="backfill-matches">
                  {preview.matches.map((m) => (
                    <li key={m.mohalla_id} className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2.5 py-1.5">
                      <span className="font-medium">{m.mohalla_name}</span>
                      <span className="text-muted-foreground">
                        {n(m.count)}
                        {strategy === "address" && (m.high || m.low) ? (
                          <span className="ml-1" data-testid={`backfill-match-conf-${m.mohalla_id}`}>
                            (<span className="text-emerald-700 dark:text-emerald-300">{n(m.high)} high</span>{m.low ? <> · <span className="text-orange-700 dark:text-orange-300">{n(m.low)} low</span></> : null})
                          </span>
                        ) : null}
                        {" "}· e.g. {(m.sample || []).slice(0, 3).join(", ")}
                      </span>
                    </li>
                  ))}
                  {preview.fallback && (
                    <li className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2.5 py-1.5">
                      <span className="font-medium">{preview.fallback.mohalla_name} <span className="text-muted-foreground font-normal">(fallback)</span></span>
                      <span className="text-muted-foreground">{n(preview.fallback.count)} unmatched</span>
                    </li>
                  )}
                </ul>
              )}
              {strategy === "address" && (preview.rows_total || 0) > 0 && (
                <ConfidenceList preview={preview} />
              )}
              {(preview.ambiguous_count > 0 || preview.unmatched_count > 0) && (
                <p className="text-xs text-muted-foreground" data-testid="backfill-skipped">
                  {preview.ambiguous_count > 0 && <>{n(preview.ambiguous_count)} skipped — address mentions several mohallas{preview.ambiguous_sample?.length ? ` (e.g. ${preview.ambiguous_sample.slice(0, 2).map((a) => a.pin).join(", ")})` : ""}. </>}
                  {preview.unmatched_count > 0 && <>{n(preview.unmatched_count)} left empty — nothing matched{preview.unmatched_sample?.length ? ` (e.g. ${preview.unmatched_sample.slice(0, 2).map((a) => a.pin).join(", ")})` : ""}. Pick a fallback mohalla to fill them too.</>}
                </p>
              )}
              {preview.missing === 0 && <p className="text-xs text-muted-foreground">Every property of this ward already has a mohalla.</p>}
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1 text-sm" data-testid="backfill-result">
              <p className="font-medium flex items-center gap-1.5 text-emerald-800 dark:text-emerald-300"><CheckCircle2 className="h-4 w-4" /> {n(result.filled)} propert{result.filled === 1 ? "y" : "ies"} filled</p>
              {(result.low_confidence > 0 || result.excluded_low > 0) && (
                <p className="text-xs text-orange-800 dark:text-orange-300" data-testid="backfill-result-confidence">
                  {result.low_confidence > 0 && <>{n(result.low_confidence)} low-confidence match{result.low_confidence === 1 ? "" : "es"} included after your confirmation — verify them on the property pages. </>}
                  {result.excluded_low > 0 && <>{n(result.excluded_low)} low-confidence match{result.excluded_low === 1 ? "" : "es"} left out — fix the address or fill them one by one.</>}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Assignments: {n(result.sync?.assigned)} newly assigned, {n(result.sync?.moved)} moved to the collector who now covers them, {n((result.sync?.no_collector || 0) + (result.sync?.uncovered || 0))} still without a covering collector.
                {result.collectors?.length ? ` Gained: ${result.collectors.slice(0, 4).map((c) => `${c.name} +${n(c.assigned)}`).join(", ")}.` : ""}
              </p>
              <Button size="sm" variant="outline" className="h-7 text-xs mt-1" onClick={() => undo(result.run_id)} disabled={busy} data-testid="backfill-undo-button">
                <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo this run
              </Button>
            </div>
          )}

          {wardId && runs.length > 0 && !result && (
            <div className="space-y-1" data-testid="backfill-runs">
              <p className="text-xs font-medium text-muted-foreground">Earlier runs for this ward</p>
              <ul className="divide-y rounded-lg border text-xs">
                {runs.slice(0, 4).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5" data-testid={`backfill-run-${r.id}`}>
                    <span>{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · {r.strategy === "single" ? "one mohalla" : r.strategy === "upload" ? `Excel upload${r.ward_names?.length > 1 ? ` (${r.ward_names.length} wards)` : ""}` : "address match"} · {n(r.filled)} filled{r.undone_at ? " · undone" : ""}</span>
                    {!r.undone_at && <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => undo(r.id)} disabled={busy} data-testid={`backfill-run-undo-${r.id}`}><Undo2 className="h-3 w-3 mr-1" /> Undo</Button>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="backfill-close-button">Close</Button>
            {!result && <Button onClick={onFillClick} disabled={!canApply} data-testid="backfill-apply-button"><Wand2 className="h-4 w-4 mr-2" /> {busy ? "Filling..." : `Fill ${preview?.will_fill ? n(preview.will_fill) : ""} propert${preview?.will_fill === 1 ? "y" : "ies"}`}</Button>}
          </div>
        </div>
      </DialogContent>

      {/* Phase 187 — low-confidence matches need an explicit confirmation before filling */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="max-w-2xl" data-testid="backfill-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-800 dark:text-orange-300">
              <AlertTriangle className="h-5 w-5" /> {n(lowRows.length)} low-confidence match{lowRows.length === 1 ? "" : "es"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p data-testid="backfill-confirm-warning">
                  <span className="font-medium text-foreground">Address match uncertain — please verify before filling.</span>{" "}
                  For these properties the mohalla name appears only partially or alongside another area name. Fill them anyway,
                  or fill only the {n(preview?.confidence?.high || 0)} high-confidence match{(preview?.confidence?.high || 0) === 1 ? "" : "es"}
                  {preview?.fallback ? ` (plus ${n(preview.fallback.count)} by fallback)` : ""} and handle these one by one.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border max-h-64 overflow-y-auto divide-y text-xs" data-testid="backfill-confirm-list">
            {lowRows.slice(0, 100).map((r) => (
              <div key={r.property_id} className="px-2.5 py-1.5 flex flex-wrap items-start justify-between gap-x-3 gap-y-0.5 bg-orange-50/50 dark:bg-orange-950/10" data-testid={`backfill-confirm-row-${r.pin}`}>
                <div className="min-w-0">
                  <span className="font-mono font-medium">{r.pin}</span>
                  <span className="text-muted-foreground"> · {r.address || "no address"}</span>
                  <span className="block text-[11px] text-orange-800 dark:text-orange-300">{r.reason}</span>
                </div>
                <span className="whitespace-nowrap inline-flex items-center gap-1"><ArrowRight className="h-3 w-3" /> {r.mohalla_name}</span>
              </div>
            ))}
            {lowRows.length > 100 && <p className="px-2.5 py-1.5 text-[11px] text-muted-foreground">+{n(lowRows.length - 100)} more — all are listed in the preview above.</p>}
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel data-testid="backfill-confirm-cancel">Cancel</AlertDialogCancel>
            {(preview?.will_fill || 0) - lowRows.length > 0 && (
              <Button variant="outline" onClick={() => { setConfirmOpen(false); apply(false); }} data-testid="backfill-confirm-high-only">
                Fill high-confidence only ({n((preview?.will_fill || 0) - lowRows.length)})
              </Button>
            )}
            <AlertDialogAction onClick={() => { setConfirmOpen(false); apply(true); }} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="backfill-confirm-fill-all">
              Fill all {n(preview?.will_fill || 0)} including low confidence
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

const CONF = {
  high: { label: "High confidence", cls: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900" },
  low: { label: "Low confidence", cls: "bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-900" },
};

/** Phase 187 — every address-matched property with its confidence; low-confidence rows first (always visible, with the
 *  warning), high-confidence rows behind a toggle. */
function ConfidenceList({ preview }) {
  const [showHigh, setShowHigh] = useState(false);
  const rows = preview.rows || [];
  const low = rows.filter((r) => r.confidence === "low");
  const high = rows.filter((r) => r.confidence === "high");
  const Row = ({ r }) => {
    const c = CONF[r.confidence] || CONF.low;
    return (
      <li className={`px-2.5 py-1.5 space-y-0.5 ${r.confidence === "low" ? "bg-orange-50/50 dark:bg-orange-950/10" : ""}`}
        data-testid={`backfill-row-${r.pin}`} data-confidence={r.confidence}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className="font-mono font-medium">{r.pin}</span>
            <span className="text-muted-foreground"> · {r.address || "no address"}</span>
            <span className="inline-flex items-center gap-1 ml-1 text-muted-foreground"><ArrowRight className="h-3 w-3" /> {r.mohalla_name}</span>
          </div>
          <Badge variant="outline" className={`text-[10px] gap-1 whitespace-nowrap shrink-0 ${c.cls}`} data-testid={`backfill-row-badge-${r.pin}`}>
            {r.confidence === "low" ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />} {c.label}
          </Badge>
        </div>
        {r.confidence === "low" && (
          <span className="block text-[11px] text-orange-800 dark:text-orange-300" data-testid={`backfill-row-warning-${r.pin}`}>
            {preview.low_warning || "Address match uncertain — please verify before filling."} <span className="text-muted-foreground">({r.reason})</span>
          </span>
        )}
      </li>
    );
  };
  return (
    <div className="space-y-1" data-testid="backfill-confidence-list">
      <p className="text-xs font-medium text-muted-foreground">Matched properties</p>
      <ul className="divide-y rounded-lg border text-xs max-h-56 overflow-y-auto">
        {low.map((r) => <Row key={r.property_id} r={r} />)}
        {high.length > 0 && (
          <li className="px-2.5 py-1.5 bg-muted/30">
            <button type="button" onClick={() => setShowHigh((v) => !v)} aria-expanded={showHigh}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              data-testid="backfill-toggle-high">
              {showHigh ? "Hide" : "Show"} {n(high.length)} high-confidence match{high.length === 1 ? "" : "es"}
            </button>
          </li>
        )}
        {showHigh && high.map((r) => <Row key={r.property_id} r={r} />)}
      </ul>
      {preview.rows_total > rows.length && (
        <p className="text-[11px] text-muted-foreground">Showing {n(rows.length)} of {n(preview.rows_total)} matches (low confidence first).</p>
      )}
    </div>
  );
}

/** Loads the ward's mohallas for the "one mohalla for all" picker before any preview exists. */
function MohallaLoader({ code, wardId, onLoaded }) {
  useEffect(() => {
    api.get(`/t/${code}/mohalla-backfill/preview`, { params: { ward_id: wardId, strategy: "address" } })
      .then((r) => onLoaded(r.data.mohallas || [])).catch(() => onLoaded([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, wardId]);
  return null;
}
